import { useEffect, useState, useCallback } from "react";
import { adminLogin, getToken, clearToken, fetchTaxOverview, fetchTaxCalendar } from "@/lib/api";

const inputCls = "w-full bg-transparent border border-[rgba(180,139,63,0.35)] px-3 py-2 text-[var(--bone)] outline-none focus:border-[var(--gold)] mb-2";
const errMsg = (d, f) => (typeof d === "string" ? d : d?.message || d?.detail || f || "Something went wrong.");
const Unknown = () => <span className="italic text-[#e88f6a]" data-testid="unknown-token">UNKNOWN</span>;
// UNKNOWN stays UNKNOWN (never $0). Known amounts get thousands separators + explicit currency code.
const money = (v, currency) => {
  if (v == null) return <Unknown />;
  const n = Number(v);
  const body = Number.isFinite(n) ? n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : String(v);
  return `${body} ${currency || "UNKNOWN CCY"}`;
};

function Login({ onIn }) {
  const [email, setEmail] = useState(""); const [pw, setPw] = useState(""); const [err, setErr] = useState(null);
  const submit = async (e) => { e.preventDefault(); setErr(null);
    try { await adminLogin(email, pw); onIn(); } catch (e) { setErr(errMsg(e.response?.data?.detail, "Login failed.")); } };
  return (
    <div className="min-h-screen bg-[var(--navy)] flex items-center justify-center px-4">
      <form onSubmit={submit} className="w-full max-w-sm" data-testid="tax-login-form">
        <h1 className="font-display uppercase tracking-wide2 text-[var(--gold)] text-xl mb-4">Founder Tax Compliance</h1>
        <input className={inputCls} type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="tax-login-email" aria-label="Email" />
        <input className={inputCls} type="password" placeholder="Password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="tax-login-password" aria-label="Password" />
        {err && <p className="text-[#e88f6a] text-sm mb-2" data-testid="tax-login-error">{err}</p>}
        <button className="jmh-cta w-full" data-testid="tax-login-submit">Enter</button>
      </form>
    </div>
  );
}

const Card = ({ label, value, testid }) => (
  <div className="border border-[rgba(180,139,63,0.18)] px-4 py-3" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wide2 text-[var(--muted)] mb-1">{label}</div>
    <div className="text-sm text-[var(--bone)]">{value}</div>
  </div>
);

function SourcePanel({ title, items, testid }) {
  return (
    <div className="border border-[rgba(180,139,63,0.18)] px-4 py-3" data-testid={testid}>
      <h3 className="font-display uppercase tracking-wide2 text-[11px] text-[var(--gold)]/80 mb-2">{title}</h3>
      {(!items || items.length === 0) ? (
        <p className="text-xs text-[var(--muted)]" data-testid={`${testid}-empty`}>None</p>
      ) : (
        <ul className="space-y-1">
          {items.map((s, i) => (
            <li key={s.source_fact_id || i} className="text-xs text-[var(--bone)] flex justify-between gap-2" data-testid={`${testid}-item`}>
              <span className="truncate">{s.jurisdiction || "—"} · {s.tax_year || "—"}</span>
              <span className="text-[var(--gold-lite)]">{s.estimated_optimization_value != null ? money(s.estimated_optimization_value, s.currency) : (s.evidence_class || "SOURCE_FACT")}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [ov, setOv] = useState(null); const [cal, setCal] = useState(null); const [err, setErr] = useState(null);
  const load = useCallback(async () => {
    setErr(null);
    try {
      const [o, c] = await Promise.all([fetchTaxOverview(), fetchTaxCalendar()]);
      setOv(o); setCal(c);
    } catch (e) {
      if (e.response?.status === 401) { clearToken(); onLogout(); return; }
      setErr(errMsg(e.response?.data?.detail, "Failed to load tax compliance."));
    }
  }, [onLogout]);
  useEffect(() => { load(); }, [load]);

  if (err) return <div className="min-h-screen bg-[var(--navy)] flex items-center justify-center"><p className="text-[#e88f6a]" data-testid="tax-error">{err}</p></div>;
  if (!ov) return <div className="min-h-screen bg-[var(--navy)] flex items-center justify-center"><p className="text-[var(--muted)]">Loading…</p></div>;

  const ts = ov.truth_strip || {};
  const sa = ov.strategic_assets || {};
  const views = cal?.views || {};
  return (
    <div className="min-h-screen bg-[var(--navy)] text-[var(--bone)] px-4 sm:px-8 py-8" data-testid="tax-view">
      <header className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="font-display uppercase tracking-wide2 text-[var(--gold)] text-xl">Founder Tax Compliance</h1>
        <button onClick={() => { clearToken(); onLogout(); }} className="text-xs uppercase tracking-wide2 text-[var(--muted)] hover:text-[var(--gold)]" data-testid="tax-logout">Sign out</button>
      </header>

      <p className="text-xs text-[var(--muted)] mb-6 max-w-3xl" data-testid="tax-authority-statement">{ov.authority_statement}</p>

      {/* Overview */}
      <section className="mb-8" data-testid="tax-overview">
        <h2 className="font-display uppercase tracking-wide2 text-[11px] text-[var(--gold)]/80 mb-3">Overview</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card label="Entities in scope" value={ov.entities_in_scope} testid="ov-entities" />
          <Card label="UNKNOWN dates" value={ov.unknown_dates} testid="ov-unknown-dates" />
          <Card label="Evidence gaps (UNKNOWN amt)" value={ov.evidence_gaps} testid="ov-evidence-gaps" />
          <Card label="Complete (reconciled)" value={ts.complete ?? 0} testid="ov-complete" />
        </div>
      </section>

      {/* Tax Truth Strip — each stage visibly separate */}
      <section className="mb-8" data-testid="tax-truth-strip">
        <h2 className="font-display uppercase tracking-wide2 text-[11px] text-[var(--gold)]/80 mb-3">Tax Truth Strip</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
          {[["Identified", "identified"], ["Prof. reviewed", "professionally_reviewed"], ["Founder authorized", "founder_authorized"],
            ["Filed", "filed"], ["Accepted", "accepted"], ["Payment recorded", "payment_recorded"],
            ["Payment reconciled", "payment_reconciled"], ["Complete", "complete"]].map(([l, k]) => (
            <Card key={k} label={l} value={ts[k] ?? 0} testid={`truth-${k}`} />
          ))}
        </div>
      </section>

      {/* Strategic Assets Intelligence */}
      <section className="mb-8" data-testid="tax-sa-intelligence">
        <h2 className="font-display uppercase tracking-wide2 text-[11px] text-[var(--gold)]/80 mb-3">Strategic Assets Intelligence</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          <SourcePanel title="Compliance facts" items={sa.COMPLIANCE_FACT} testid="sa-compliance" />
          <SourcePanel title="Opportunities" items={sa.TAX_OPPORTUNITY} testid="sa-opportunities" />
          <SourcePanel title="Risks" items={sa.RISK_SIGNAL} testid="sa-risks" />
          <SourcePanel title="Information requests" items={sa.INFORMATION_REQUEST} testid="sa-info-requests" />
          <SourcePanel title="Unresolved conflicts" items={sa.unresolved_conflicts} testid="sa-conflicts" />
        </div>
      </section>

      {/* Calendar */}
      <section className="mb-8" data-testid="tax-calendar">
        <h2 className="font-display uppercase tracking-wide2 text-[11px] text-[var(--gold)]/80 mb-3">Calendar</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
          {[["Overdue", "overdue"], ["Next 7 days", "next_7_days"], ["Next 30 days", "next_30_days"],
            ["Next 90 days", "next_90_days"], ["Awaiting review", "awaiting_professional_review"],
            ["UNKNOWN dates", "unknown_date"]].map(([l, k]) => (
            <Card key={k} label={l} value={(views[k] || []).length} testid={`cal-${k}`} />
          ))}
        </div>
        <p className="text-[11px] text-[var(--muted)] mt-2">{cal?.notes}</p>
      </section>

      <p className="text-[11px] text-[var(--muted)] max-w-3xl" data-testid="tax-boundaries">{ov.boundaries}</p>
    </div>
  );
}

export default function FounderTaxCompliance() {
  const [authed, setAuthed] = useState(!!getToken());
  if (!authed) return <Login onIn={() => setAuthed(true)} />;
  return <Dashboard onLogout={() => setAuthed(false)} />;
}
